/**
 * The chips that outlive the box: what a committed draft's commands are still
 * steering, plus the two facts a seed wrote into the exploration itself.
 *
 * A draft's /commands mint chips while the draft exists, and the seed presses
 * write what they asked into the tune controls BEFORE clearing it — that is
 * `applySpec`'s contract. But the chips themselves used to die with the draft for
 * every command the tune panel carries a dial for, on the reasoning that the dial
 * was now the visible record. It was not: the panel is closed at the moment of
 * the press, so somebody who typed seven commands watched six pills vanish and
 * one survive, and could not tell honoured from dropped. The run record said
 * honoured — the manifest carries the budget — but a record that takes an export
 * to read is not a surface.
 *
 * So one function computes the whole committed row. The chip condition is
 * "differs from an untouched workspace", not "was typed": the controls are one
 * state with two writers — commands and dials — and a chip that tried to remember
 * which writer set a value would drift from the value the moment the other writer
 * moved it. The accepted cost is that `/moves 24` over a 24-move default commits
 * to a state no chip marks, because the promise it made IS the untouched state.
 * The baseline is `seededTune`, the same expression boot uses, so "untouched"
 * here and "untouched" at boot cannot come to mean two different workspaces.
 *
 * Every chip carries where its fact is ALREADY stated, in `restates`, and each
 * surface filters out what it would duplicate: the rail's MOVES and BUDGET chips
 * stand under the input at all times, so the bar row showing "48 moves" above
 * them would be D19's retired mode chip again — the same fact twice on one
 * surface — and the tune panel's dials likewise already carry the counts. What
 * remains on each surface is exactly the steering that surface would otherwise
 * say nothing about.
 *
 * Pure, and a chip is DATA — `clear` NAMES what removing the chip un-sets and the
 * composer dispatches it. The same posture `CueAction` holds and for a related
 * reason: nothing in this set can reach a store, so the whole row is testable
 * against `parseCommands` without one, and the test that holds the two surfaces'
 * wording together (see steerChips.test.ts) is a string comparison rather than a
 * render.
 */

import { seededTune, type RunTune, type TuneDefaults } from '../core/store'
import { untilLabel, type Directive, type Frame } from '../core/types'
import type { CommandSpec, RunControls } from '../engine/command'

/** Which store write removing a chip performs. Data, never a closure — see above. */
export type CommittedClear =
  | {
      kind: 'tune'
      field:
        | 'moves'
        | 'checks'
        | 'panels'
        | 'audits'
        | 'conjectures'
        | 'mode'
        | 'ladder'
        | 'depth'
        | 'until'
    }
  | { kind: 'ceiling' }
  | { kind: 'brief'; at: number }

export interface CommittedChip {
  /** Stable identity for the row, and for React. */
  key: string
  label: string
  /** The full text where the label truncates it. */
  title?: string
  /**
   * Where this fact is already stated, so each surface can drop what it would
   * duplicate: `'rail'` for the chip rail's own MOVES and BUDGET (always visible
   * under the input — the bar row filters these), `'panel'` for a tune-panel
   * dial or its Standing brief section (the panel's Steering section filters
   * these), `null` for steering no other surface states, which shows everywhere.
   */
  restates: 'rail' | 'panel' | null
  /** Null for the one fact with nothing to un-set: a frame has no setter, because
   *  re-reading an opened graph's seed is not a chip-sized act. */
  clear: CommittedClear | null
  /** What pressing the chip itself opens: a brief chip — the one chip, or a
   *  directive's own — opens the tune panel at its Standing brief section, where
   *  a directive is reworded. Data, like `clear`. */
  open?: 'brief'
}

/**
 * How many directives the row shows one chip each before it shows the brief as
 * one chip. A brief at its cap was twelve pills: seven rows above the input at
 * 1440, nine on a phone — half the screen, over the canvas's own controls while
 * Adjust was open. Each directive keeps its ✕ in Adjust's Standing brief section,
 * which the one chip opens.
 */
export const BRIEF_CHIPS_MAX = 3

/**
 * The projection the seed press commits — `explore` writes exactly this into
 * the tune controls before clearing the box. ONE expression,
 * shared with the suite, because the original bug's shape was a press and a
 * display disagreeing about what had been committed: a press that hand-built
 * this object could drop an optional field and compile, and the pill would die
 * at the press again with every pure test still green.
 */
export function committedTune(live: RunControls & Pick<RunTune, 'execution' | 'detail'>): RunTune {
  return {
    moves: live.moves,
    checks: live.checks,
    panels: live.panels,
    audits: live.audits,
    ...(live.conjectures ? { conjectures: live.conjectures } : {}),
    execution: live.execution,
    detail: live.detail,
    mode: live.mode,
    ladder: live.ladder,
    depth: live.depth,
    until: live.until,
  }
}

/** command.ts's own wording, verbatim — the parity test holds the two together. */
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`

/** command.ts's truncation, verbatim, so a directive's pill does not reword
 *  itself the moment the box commits it. */
const clip = (t: string) => (t.length > 34 ? `${t.slice(0, 33)}…` : t)

/**
 * The committed row, in the tune panel's own top-to-bottom order — brief, moves,
 * checks, panels, audits, ceiling, then the steering the panel has no dial for —
 * with the frame in front, because it is the one fact about the exploration
 * rather than the next run.
 *
 * `spec` is the CURRENT draft's: a field the draft carries again belongs to the
 * draft's own chip, so its committed twin stands down rather than standing beside
 * it saying something the next press will overwrite. The same guard the original
 * three chips wore, applied uniformly.
 */
export function committedChips(args: {
  spec: CommandSpec
  tune: RunTune
  /** The committed ceiling, as the store holds it — a string, empty for none. */
  ceiling: string
  defaults: TuneDefaults
  frame: Frame | undefined
  brief: readonly Directive[] | undefined
}): CommittedChip[] {
  const { spec, tune, ceiling, defaults, frame, brief } = args
  const seeded = seededTune(defaults)
  const chips: CommittedChip[] = []

  // 'claim' is the unmarked default reading, like panels 0 or an empty ceiling:
  // only the two frames somebody chose earn a pill. "Seeded as", not the draft
  // chip's "read as": the draft chip promises how the NEXT press will read its
  // words, this pill states how THIS exploration already was — two facts, and
  // one string for both left the difference to a hover title nobody owes us.
  if (spec.frame === undefined && frame !== undefined && frame !== 'claim') {
    chips.push({
      key: 'frame',
      label: `seeded as a ${frame}`,
      title: 'How this exploration reads its seed — chosen when it was opened',
      restates: null,
      clear: null,
    })
  }
  // Suppressed under a frame command, not per-directive: a frame command makes
  // the draft a NEW seed, and `runSeed` gives the new exploration the draft's
  // directives ONLY — so these pills would promise a brief the press is about
  // to discard, which is the honoured-vs-dropped ambiguity this module exists
  // to close. (⌘↵ seeds too and cannot be seen from here; the typed door is
  // the one this parser owns.)
  if (spec.frame === undefined && (brief?.length ?? 0) > BRIEF_CHIPS_MAX) {
    chips.push({
      key: 'brief',
      label: `standing brief · ${brief!.length}`,
      title: brief!.map((d) => `${d.kind}: ${d.text}`).join('\n'),
      restates: 'panel',
      clear: null,
      open: 'brief',
    })
  } else if (spec.frame === undefined) {
    for (const [at, d] of (brief ?? []).entries()) {
      // Its ✕ removes it here; its words open the list, which is where it is
      // reworded — with three or fewer the one chip does not stand, and the list
      // would otherwise be reachable from ⌘K alone.
      chips.push({
        key: `brief#${at}`,
        label: `${d.kind}: ${clip(d.text)}`,
        title: `${d.kind}: ${d.text}`,
        restates: 'panel',
        clear: { kind: 'brief', at },
        open: 'brief',
      })
    }
  }
  if (spec.moves === undefined && tune.moves !== seeded.moves) {
    chips.push({
      key: 'moves',
      label: `${tune.moves} moves`,
      restates: 'rail',
      clear: { kind: 'tune', field: 'moves' },
    })
  }
  // Defined-and-different, not merely different: `undefined` means "whatever the
  // move count implies", which is the ship default — an unmarked state even in a
  // workspace whose own default asks for more.
  if (spec.checks === undefined && tune.checks !== undefined && tune.checks !== seeded.checks) {
    chips.push({
      key: 'checks',
      label:
        tune.checks === 'every' ? 'check every claim it can' : plural(tune.checks, 'source check'),
      restates: 'panel',
      clear: { kind: 'tune', field: 'checks' },
    })
  }
  if (spec.panels === undefined && tune.panels !== seeded.panels) {
    chips.push({
      key: 'panels',
      label: plural(tune.panels, 'panel'),
      restates: 'panel',
      clear: { kind: 'tune', field: 'panels' },
    })
  }
  if (spec.audits === undefined && tune.audits !== seeded.audits) {
    chips.push({
      key: 'audits',
      label: plural(tune.audits, 'cross-examination'),
      restates: 'panel',
      clear: { kind: 'tune', field: 'audits' },
    })
  }
  // Any at all, since none is the only default there is (D281).
  if (spec.conjectures === undefined && (tune.conjectures ?? 0) > 0) {
    chips.push({
      key: 'conjectures',
      label: plural(tune.conjectures!, 'insight pass', 'insight passes'),
      restates: 'panel',
      clear: { kind: 'tune', field: 'conjectures' },
    })
  }
  // Defined-and-different, like checks: an absent mode means auto, which is
  // what an untyped draft already means — an unmarked state. The two labels are
  // command.ts's own, so a typed /confirm keeps its words across the commit.
  if (spec.mode === undefined && tune.mode !== undefined && tune.mode !== seeded.mode) {
    chips.push({
      key: 'mode',
      label: tune.mode === 'confirm' ? 'asks before each move' : 'runs on its own',
      restates: 'panel',
      clear: { kind: 'tune', field: 'mode' },
    })
  }
  // `Number`, not `parseFloat`, because this pill is a promise about the RUN
  // press, and `ceilingSet` — the press's own predicate — uses Number: on a
  // string Number rejects and parseFloat salvages, a pill would promise a cap
  // no run enforces.
  const cap = ceiling ? Number(ceiling) : Number.NaN
  if (spec.usdMax === undefined && Number.isFinite(cap) && cap > 0) {
    chips.push({
      key: 'ceiling',
      label: `stop at $${cap}`,
      restates: 'rail',
      clear: { kind: 'ceiling' },
    })
  }
  if (spec.ladder === undefined && tune.ladder?.length) {
    // One verb wears /only's wording, not "ladder: challenge" — the committed
    // state cannot remember which of the two commands set it, and "only" is the
    // truer description of a one-verb run. The accepted cost is that a typed
    // `/ladder challenge` rewords at the press; steerChips.test.ts pins it.
    chips.push({
      key: 'ladder',
      label:
        tune.ladder.length === 1 ? `only ${tune.ladder[0]}` : `ladder: ${tune.ladder.join(' → ')}`,
      restates: null,
      clear: { kind: 'tune', field: 'ladder' },
    })
  }
  if (spec.depth === undefined && tune.depth !== undefined) {
    chips.push({
      key: 'depth',
      label: `${plural(tune.depth, 'ring')} deep`,
      restates: null,
      clear: { kind: 'tune', field: 'depth' },
    })
  }
  if (spec.until === undefined && tune.until) {
    chips.push({
      key: 'until',
      label: `until ${untilLabel(tune.until)}`,
      restates: null,
      clear: { kind: 'tune', field: 'until' },
    })
  }
  return chips
}

/**
 * What removing a tune chip writes back: the seeded default, which is the same
 * state as never having touched the control. One expression against the same
 * `seededTune` the chip condition compares to — a reset that landed anywhere else
 * would re-mint the chip it just removed, or silently steer. (A field added to
 * `CommittedClear` without a case here is a compile error — the switch has no
 * default and must return, the same mechanism `endsTheRun` uses.)
 */
export function resetPatch(
  field: Extract<CommittedClear, { kind: 'tune' }>['field'],
  defaults: TuneDefaults,
): Partial<RunTune> {
  const seeded = seededTune(defaults)
  switch (field) {
    case 'moves':
      return { moves: seeded.moves }
    case 'checks':
      return { checks: seeded.checks }
    case 'panels':
      return { panels: seeded.panels }
    case 'audits':
      return { audits: seeded.audits }
    case 'conjectures':
      return { conjectures: undefined }
    case 'mode':
      return { mode: seeded.mode }
    case 'ladder':
      return { ladder: undefined }
    case 'depth':
      return { depth: undefined }
    case 'until':
      return { until: undefined }
  }
}
